/**
 * <cd-all-reviews-fetcher>
 *
 * Drives the shop-wide reviews page (page.judgeme_all_reviews.json).
 *
 * Architecture (matches Judge.me's own all-reviews widget pattern):
 *   - SSR ships an empty list. No reviews in the initial HTML.
 *   - On connect we fetch page 1 from Judge.me's public widget API
 *     (`/widgets/all_reviews_page`) — same widget HTML format as the
 *     PDP fetcher's `/widgets/product_review` endpoint.
 *   - An IntersectionObserver watches a sentinel at the bottom of the
 *     list. When it comes within 600px of the viewport, we fetch the
 *     next page and append. Repeat until the API returns a partial or
 *     empty page.
 *   - Sort change clears everything, refetches from page 1 with the
 *     new `sort_by` param.
 *   - Photos render with native `loading="lazy"` and explicit width/
 *     height; we never rewrite `src` after parse, so native lazy is
 *     preserved.
 *
 * API:
 *   GET https://judge.me/api/v1/widgets/all_reviews_page
 *     ?api_token=<public_token>
 *     &shop_domain=<shop>.myshopify.com
 *     &platform=shopify
 *     &page=<n>
 *     &per_page=<n>
 *     &sort_by=<token>     (see SORT_PARAMS below)
 *     &sort_dir=<asc|desc> (pairs with rating sorts)
 *
 * Response shape: { "all_reviews": "<html...>" } — same card markup
 * as the per-product widget endpoint.
 *
 * SORT TOKENS — extracted from Judge.me's own widget code
 * (cdnwidget.judge.me/widget/common.js) by reading the dropdown
 * change handler's mapping table. The dropdown emits hyphenated
 * tokens (e.g., `pictures-first`) but the API expects underscored
 * ones (`pictures_first`). Earlier we sent `sort_by=pictures` which
 * the API silently ignores, so every sort returned the same default
 * (chronological) order.
 */
(function () {
  if (customElements.get('cd-all-reviews-fetcher')) return;
  if (!window.CdJudgemeFetcher) {
    console.error('cd-all-reviews-fetcher: requires cd-judgeme-fetcher.js loaded first');
    return;
  }

  // Shared widget-parsing helpers — see assets/cd-judgeme-fetcher.js.
  var pickAttr = window.CdJudgemeFetcher.pickAttr;
  var pickText = window.CdJudgemeFetcher.pickText;
  var formatDate = window.CdJudgemeFetcher.formatDate;
  var escapeHtml = window.CdJudgemeFetcher.escapeHtml;
  var decodeEntities = window.CdJudgemeFetcher.decodeEntities;
  var upgradePicUrl = window.CdJudgemeFetcher.upgradePicUrl;

  // Dropdown value → API params. Tokens match Judge.me's internal
  // mapping table; do NOT use the dropdown's hyphenated values
  // here — the API silently falls through to default order if it
  // doesn't recognize the token.
  var SORT_PARAMS = {
    pictures:       { sort_by: 'pictures_first' },
    newest:         { sort_by: 'created_at', sort_dir: 'desc' },
    highest_rating: { sort_by: 'rating',     sort_dir: 'desc' },
    lowest_rating:  { sort_by: 'rating',     sort_dir: 'asc' }
  };

  function parseReviews(widgetHtml) {
    if (!widgetHtml) return [];
    var chunks = widgetHtml.split("<div class='jdgm-rev jdgm-divider-top'");
    var out = [];
    for (var i = 1; i < chunks.length; i++) {
      var c = chunks[i];
      var id = pickAttr(c, 'data-review-id');
      if (!id) continue;
      var verified = pickAttr(c, 'data-verified-buyer') === 'true';
      var score = parseInt(pickAttr(c, 'data-score'), 10);
      if (isNaN(score) || score < 1) score = 5;
      if (score > 5) score = 5;

      var tsMatch = c.match(/jdgm-rev__timestamp[^>]*data-content='([^']*)'/);
      var date = tsMatch ? formatDate(tsMatch[1]) : '';
      var author = pickText(c, "jdgm-rev__author'>", '</span>') || 'Anonymous';
      var title = pickText(c, "jdgm-rev__title'>", '</b>');
      var body = pickText(c, "jdgm-rev__body'>", '</div>');
      var loc = '';
      var locMatch = c.match(/jdgm-rev__location'[^>]*>([\s\S]*?)<\/span>/);
      if (locMatch) {
        loc = locMatch[1].replace(/<[^>]+>/g, '').replace(/[()]/g, '').trim();
      }
      var pics = [];
      var picsMatch = c.match(/jdgm-rev__pics[^>]*>([\s\S]*?)<\/div>/);
      if (picsMatch) {
        // Judge.me's all-reviews widget puts the real URL in
        // `data-src` (lazy-load placeholder pattern), with `src`
        // either missing or holding a 1px gif. Prefer `data-src` and
        // fall back to `src`.
        var imgRe = /<img[^>]+(?:data-src|src)=['"]([^'"]+)['"]/g;
        var m;
        while ((m = imgRe.exec(picsMatch[1])) !== null) pics.push(m[1]);
      }
      // Product link — Judge.me's all-reviews widget uses
      // `jdgm-rev__prod-link` (note: `prod`, not `product`). Inside
      // a `jdgm-rev__prod-info-wrapper` block on each card.
      var productHref = '';
      var productTitle = '';
      var prodMatch = c.match(/jdgm-rev__prod-link['"][^>]*href=['"]([^'"]+)['"][^>]*>([\s\S]*?)<\/a>/);
      if (!prodMatch) {
        // Fallback for variant where href comes before class.
        prodMatch = c.match(/href=['"]([^'"]+)['"][^>]*class=['"][^'"]*jdgm-rev__prod-link[^'"]*['"][^>]*>([\s\S]*?)<\/a>/);
      }
      if (prodMatch) {
        productHref = decodeEntities(prodMatch[1]);
        productTitle = decodeEntities(prodMatch[2].replace(/<[^>]+>/g, '').trim());
      }

      out.push({
        id: id, score: score, verified: verified, date: date, author: author,
        title: title, body: body, location: loc, pics: pics,
        productHref: productHref, productTitle: productTitle
      });
    }
    return out;
  }

  function buildCard(review) {
    var stars = '';
    for (var s = 1; s <= 5; s++) {
      stars += '<span class="cd-product-reviews__star' + (s <= review.score ? ' is-filled' : '') + '" aria-hidden="true">★</span>';
    }

    var verifiedHtml = review.verified
      ? '<span class="cd-product-reviews__verified" title="Verified purchase" aria-label="Verified purchase">'
        + '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12" aria-hidden="true">'
        + '<path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        + '<span class="cd-product-reviews__verified-text">Verified</span></span>'
      : '';

    var titleHtml = review.title ? '<h3 class="cd-product-reviews__title">' + escapeHtml(review.title) + '</h3>' : '';
    var bodyHtml = '';
    if (review.body) {
      var isLong = review.body.length > 280;
      bodyHtml = '<p class="cd-product-reviews__body">' + escapeHtml(review.body) + '</p>';
      if (isLong) bodyHtml += '<button type="button" class="cd-product-reviews__expand" data-cd-expand>Read more</button>';
    }

    var picsHtml = '';
    if (review.pics && review.pics.length) {
      var picMax = Math.min(review.pics.length, 3);
      picsHtml = '<div class="cd-product-reviews__pics">';
      for (var p = 0; p < picMax; p++) {
        // 400/800 for the 1x/2x grid thumbnail; 1600 full-size only on click.
        var thumb = upgradePicUrl(review.pics[p], 400);
        var thumb2x = upgradePicUrl(review.pics[p], 800);
        var full = upgradePicUrl(review.pics[p], 1600);
        picsHtml += '<a class="cd-product-reviews__pic" href="' + escapeHtml(full) + '" aria-label="Open customer photo">'
          + '<img src="' + escapeHtml(thumb) + '" srcset="' + escapeHtml(thumb) + ' 1x, ' + escapeHtml(thumb2x) + ' 2x" loading="lazy" decoding="async" alt="" width="400" height="400"></a>';
      }
      picsHtml += '</div>';
    }

    var locHtml = review.location
      ? '<span class="cd-product-reviews__loc-sep" aria-hidden="true">·</span><span class="cd-product-reviews__location">' + escapeHtml(review.location) + '</span>'
      : '';
    var dateHtml = review.date ? '<span class="cd-product-reviews__date">' + escapeHtml(review.date) + '</span>' : '';
    var verifiedSep = (review.verified && (review.date || review.location))
      ? '<span class="cd-product-reviews__loc-sep" aria-hidden="true">·</span>' : '';

    var productHtml = '';
    if (review.productHref && review.productTitle) {
      productHtml = '<a class="cd-product-reviews__product-link" href="' + escapeHtml(review.productHref) + '">'
        + escapeHtml(review.productTitle) + ' →</a>';
    }

    var li = document.createElement('li');
    li.className = 'cd-product-reviews__card-wrap is-revealed';
    li.setAttribute('data-review-id', review.id);
    li.setAttribute('data-score', String(review.score));
    li.innerHTML =
      '<article class="cd-product-reviews__card" style="--cd-card-rot: 0deg;">'
      + '<div class="cd-product-reviews__stars" role="img" aria-label="' + review.score + ' out of 5 stars">' + stars + '</div>'
      + titleHtml + bodyHtml + picsHtml
      + '<div class="cd-product-reviews__byline">'
      + '<div class="cd-product-reviews__byline-text">'
      + '<div class="cd-product-reviews__author">' + escapeHtml(review.author) + '</div>'
      + '<div class="cd-product-reviews__meta">' + dateHtml + locHtml + verifiedSep + verifiedHtml + '</div>'
      + productHtml
      + '</div></div></article>';
    return li;
  }

  // ---- Component

  class CDAllReviewsFetcher extends HTMLElement {
    connectedCallback() {
      this.token = this.getAttribute('data-public-token') || '';
      this.shopDomain = this.getAttribute('data-shop-domain') || '';
      // Hard-clamp perPage. Judge.me's API caps at 25 server-side
      // (silently — they return 25 even when you ask for more) and
      // returns just 1 review when per_page is missing/NaN/undefined.
      // Stay between [1, 25] to avoid both pitfalls.
      var rawPerPage = parseInt(this.getAttribute('data-per-page'), 10);
      this.perPage = (rawPerPage > 0 && rawPerPage <= 25) ? rawPerPage : 12;

      this.list = this.querySelector('.cd-product-reviews__list');
      this.errorEl = this.querySelector('[data-cd-reviews-error]');
      this.spinner = this.querySelector('[data-cd-reviews-spinner]');
      this.sentinel = this.querySelector('[data-cd-reviews-sentinel]');
      this.section = this.closest('.cd-product-reviews');
      this.sortSelect = this.section ? this.section.querySelector('[data-cd-reviews-sort]') : null;
      // Histogram lives in the section header (sibling of this element).
      // SSR ships an empty 5-row skeleton; we fill it from the API's
      // `all_reviews_header` HTML on the first page-1 fetch and wire
      // each row's click to refetch with `filter_rating=N`.
      this.histogram = this.section ? this.section.querySelector('[data-cd-hist]') : null;
      this.histogramRows = this.histogram ? this.histogram.querySelectorAll('[data-cd-hist-rating]') : [];
      this.histogramFilled = false;
      this.activeRating = 0;

      if (!this.list || !this.token || !this.shopDomain) return;

      this.seen = new Set();
      this.nextPage = 1;
      this.loading = false;
      this.allLoaded = false;
      // Cap how many follow-up auto-fetches `maybeFetchMore()` can fire
      // after the initial page lands. Without a cap, a tall viewport
      // can chain 4–6 pages on first load. One follow-up is enough to
      // cover the "first page didn't fill the viewport" case; further
      // pages then come from real user scroll via the IO observer.
      this.autoFillsRemaining = 1;
      // Always default to 'pictures' on a fresh load. Reading the
      // dropdown's value is unreliable here because bfcache (back/
      // forward navigation) restores form state, leaving the dropdown
      // visually showing "Pictures First" while sortSelect.value can
      // still report the previous selection. Force-sync both the
      // variable and the DOM control.
      this.currentSort = 'pictures';
      if (this.sortSelect) this.sortSelect.value = this.currentSort;

      // Bind the sort-change handler once and keep the reference so
      // `disconnectedCallback` can remove it cleanly. `bind()` returns
      // a new function each call, so without caching the bound copy
      // we'd never be able to detach it.
      this._onSortChangeBound = this.onSortChange.bind(this);
      if (this.sortSelect) this.sortSelect.addEventListener('change', this._onSortChangeBound);

      // Histogram filter — clicking a row applies `filter_rating=N` to
      // subsequent fetches and resets the list. Re-clicking the active
      // row (or sort change) clears the filter.
      var self = this;
      for (var h = 0; h < this.histogramRows.length; h++) {
        this.histogramRows[h].addEventListener('click', function () {
          if (self.loading) return;
          if (this.disabled) return;
          var rating = parseInt(this.getAttribute('data-cd-hist-rating'), 10);
          if (!rating) return;
          var next = (self.activeRating === rating) ? 0 : rating;
          self.applyRatingFilter(next);
        });
      }

      // Read-more / Read-less expand on long review bodies. Section-
      // scoped delegation — also catches dynamically appended cards.
      // Mirrors the PDP's inline expand handler so behavior matches.
      this.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-cd-expand]');
        if (!btn) return;
        var card = btn.closest('.cd-product-reviews__card');
        if (!card) return;
        var body = card.querySelector('.cd-product-reviews__body');
        if (!body) return;
        var expanded = body.classList.toggle('is-expanded');
        btn.textContent = expanded ? 'Read less' : 'Read more';
      });

      // IntersectionObserver: trigger the next page fetch when the
      // sentinel is within 200px of the viewport. After all pages are
      // loaded we disconnect the observer entirely. Earlier this was
      // 600px, but combined with the post-fetch `maybeFetchMore` the
      // chain could over-prefetch on tall monitors. 200px keeps a
      // small lead-in for smooth scrolling without front-loading
      // multiple pages on first paint.
      if ('IntersectionObserver' in window && this.sentinel) {
        var self = this;
        this.observer = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting && !self.loading && !self.allLoaded) {
              self.fetchPage();
              break;
            }
          }
        }, { rootMargin: '200px 0px' });
        this.observer.observe(this.sentinel);
      }

      // Kick off the first page right away so users see content even
      // if they never scroll (e.g., short pages on big monitors).
      this.fetchPage();
    }

    buildUrl(page) {
      // Belt-and-suspenders: re-validate perPage at URL-build time in
      // case it got mutated. Anything outside [1, 25] would cause the
      // API to return either 1 review (NaN/undefined) or 25 (default).
      var pp = (this.perPage > 0 && this.perPage <= 25) ? this.perPage : 12;
      var url = 'https://judge.me/api/v1/widgets/all_reviews_page'
        + '?api_token=' + encodeURIComponent(this.token)
        + '&shop_domain=' + encodeURIComponent(this.shopDomain)
        + '&platform=shopify'
        + '&page=' + page
        + '&per_page=' + pp;
      var params = SORT_PARAMS[this.currentSort];
      if (params) {
        if (params.sort_by) url += '&sort_by=' + encodeURIComponent(params.sort_by);
        if (params.sort_dir) url += '&sort_dir=' + encodeURIComponent(params.sort_dir);
      }
      // Histogram filter — Judge.me's all-reviews endpoint accepts the
      // same `filter_rating` param as the product-review endpoint.
      // Verified live; only this exact param name works.
      if (this.activeRating >= 1 && this.activeRating <= 5) {
        url += '&filter_rating=' + this.activeRating;
      }
      return url;
    }

    /**
     * Parse Judge.me's `all_reviews_header` HTML for the per-star
     * histogram counts and percentages, then fill the SSR'd skeleton
     * rows. Idempotent — only runs the first time we see the header.
     * Disables 0-count rows so users can't pick a filter that returns
     * an empty list.
     */
    fillHistogram(headerHtml) {
      if (this.histogramFilled || !this.histogram || !headerHtml) return;
      var rowRe = /jdgm-histogram__row[^>]*data-rating='(\d+)'[^>]*data-frequency='(\d+)'[^>]*data-percentage='(\d+)'/g;
      var found = {};
      var m;
      while ((m = rowRe.exec(headerHtml)) !== null) {
        found[m[1]] = { freq: parseInt(m[2], 10), pct: parseInt(m[3], 10) };
      }
      // If the header didn't include any histogram rows, leave the
      // skeleton in place rather than zeroing it out.
      if (!Object.keys(found).length) return;
      for (var i = 0; i < this.histogramRows.length; i++) {
        var row = this.histogramRows[i];
        var rating = row.getAttribute('data-cd-hist-rating');
        var data = found[rating] || { freq: 0, pct: 0 };
        var bar = row.querySelector('.cd-product-reviews__hist-bar-fill');
        var count = row.querySelector('.cd-product-reviews__hist-count');
        if (bar) bar.style.width = data.pct + '%';
        if (count) count.textContent = data.freq;
        row.setAttribute('aria-label', data.pct + '% (' + data.freq + ') reviews with ' + rating + ' star rating — click to filter');
        if (data.freq === 0) {
          row.disabled = true;
          row.setAttribute('aria-disabled', 'true');
        }
      }
      this.histogramFilled = true;
    }

    applyRatingFilter(rating) {
      this.activeRating = rating;
      // Paint active state immediately for instant feedback.
      for (var i = 0; i < this.histogramRows.length; i++) {
        var r = this.histogramRows[i];
        var rNum = parseInt(r.getAttribute('data-cd-hist-rating'), 10);
        var on = rating !== 0 && rNum === rating;
        r.classList.toggle('is-active', on);
        r.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      this.resetAndFetch();
    }

    resetAndFetch() {
      this.list.innerHTML = '';
      this.seen = new Set();
      this.nextPage = 1;
      this.allLoaded = false;
      this.autoFillsRemaining = 1;
      if (this.observer && this.sentinel) this.observer.observe(this.sentinel);
      this.fetchPage();
    }

    async fetchPage() {
      if (this.loading || this.allLoaded) return;
      this.loading = true;
      this.setSpinner(true);
      if (this.errorEl) this.errorEl.hidden = true;

      var page = this.nextPage;
      var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timeoutId = setTimeout(function () { if (controller) controller.abort(); }, 12000);

      try {
        var fetchOpts = { method: 'GET', headers: { Accept: 'application/json' } };
        if (controller) fetchOpts.signal = controller.signal;
        var res = await fetch(this.buildUrl(page), fetchOpts);
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var json = await res.json();
        // The all-reviews endpoint returns the widget HTML under the
        // `all_reviews` key (the per-product widget uses `widget`).
        // Fall back to `widget` for safety in case the shape changes.
        var widgetHtml = (json && (json.all_reviews || json.widget)) || '';
        var reviews = parseReviews(widgetHtml);
        // Fill the histogram from the `all_reviews_header` portion the
        // first time we see it (page 1 of any sort/filter request).
        if (!this.histogramFilled && json && json.all_reviews_header) {
          this.fillHistogram(json.all_reviews_header);
        }

        var addedCount = 0;
        var frag = document.createDocumentFragment();
        for (var i = 0; i < reviews.length; i++) {
          if (this.seen.has(reviews[i].id)) continue;
          frag.appendChild(buildCard(reviews[i]));
          this.seen.add(reviews[i].id);
          addedCount += 1;
        }
        if (addedCount > 0) this.list.appendChild(frag);

        this.nextPage = page + 1;

        // End-of-list detection: API returned fewer than perPage, OR
        // returned 0 fresh items (all duplicates). Disconnect observer
        // so we stop firing fetches.
        if (reviews.length < this.perPage || addedCount === 0) {
          this.allLoaded = true;
          if (this.observer && this.sentinel) this.observer.unobserve(this.sentinel);
        }

        // First-page-empty case — show error/empty message.
        if (page === 1 && this.list.children.length === 0 && this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = 'No reviews to show yet.';
        }
      } catch (err) {
        clearTimeout(timeoutId);
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = "Couldn't load reviews. Try again in a moment.";
        }
        if (window.console && console.warn) console.warn('[cd-all-reviews] fetch failed:', err);
      } finally {
        this.loading = false;
        this.setSpinner(false);
        // After each successful load, re-check whether the sentinel is
        // still within the trigger zone (rootMargin = 600px). The IO
        // callback only fires when the intersection ratio CHANGES, so
        // if the first page doesn't fill the viewport the sentinel
        // stays "intersecting" and the observer goes silent. This
        // manual re-poke kicks off the next page when needed and
        // chains until either the viewport is filled or all pages
        // are loaded.
        this.maybeFetchMore();
      }
    }

    /**
     * Fallback to the IO callback, which only fires when the
     * intersection ratio CHANGES — if the first page doesn't fill the
     * viewport, the sentinel stays "intersecting" and the observer
     * goes silent. This re-pokes once to top up tall viewports, then
     * yields to user scroll. Capped via `autoFillsRemaining` so a
     * very tall viewport can't chain through every page on first
     * paint (was an unbounded chain previously).
     */
    maybeFetchMore() {
      if (this.loading || this.allLoaded || !this.sentinel) return;
      if (this.autoFillsRemaining <= 0) return;
      var rect = this.sentinel.getBoundingClientRect();
      var viewportH = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top - viewportH < 200) {
        this.autoFillsRemaining -= 1;
        this.fetchPage();
      }
    }

    setSpinner(active) {
      if (!this.spinner) return;
      this.spinner.classList.toggle('is-active', active);
    }

    onSortChange(e) {
      var value = (e && e.target && e.target.value) || '';
      if (!value || value === this.currentSort) return;
      this.currentSort = value;
      // Sort change clears any active rating filter — fresh baseline.
      this.activeRating = 0;
      for (var i = 0; i < this.histogramRows.length; i++) {
        this.histogramRows[i].classList.remove('is-active');
        this.histogramRows[i].setAttribute('aria-pressed', 'false');
      }
      this.resetAndFetch();
    }

    /**
     * Tear down on element removal: stop the IntersectionObserver and
     * detach the sort-change listener. Without this, an SPA-style
     * navigation that removes the section leaves a dangling observer
     * holding a reference to the (now-orphaned) sentinel.
     */
    disconnectedCallback() {
      if (this.observer) {
        if (this.sentinel) this.observer.unobserve(this.sentinel);
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.sortSelect && this._onSortChangeBound) {
        this.sortSelect.removeEventListener('change', this._onSortChangeBound);
      }
    }
  }

  customElements.define('cd-all-reviews-fetcher', CDAllReviewsFetcher);
})();
